import { useCallback, useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload, faPen, faRotateRight, faTrash } from "@fortawesome/free-solid-svg-icons";
import html2canvas from "html2canvas";
import moment from "moment";
import {
  deleteAppointment,
  getAllAppointments,
  getAvailability,
  updateAppointment,
} from "../APIs/api-routes.util";
import { AppointmentRow, AvailabilityResponse, ApiMessageResponse } from "../APIs/api.types";
import {
  BookingType,
  availabilityMessage,
  bookingTypeLabel,
  formatTokenLabel,
} from "../common/booking.util";
import { Alert } from "../components/ui/Alert";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Modal, ModalFooter } from "../components/ui/Modal";
import { EmptyState, LoadingOverlay } from "../components/ui/PagePrimitives";
import { useAppSettings } from "../context/AppSettingsContext";
import { apiSubscribe } from "../lib/api-subscribe";

function AllAppointments() {
  const { urgentEnabled } = useAppSettings();
  const [data, setData] = useState<AppointmentRow[]>([]);
  const [alert, setAlert] = useState<{ variant: "success" | "error"; message: string } | null>(null);
  const [loading, setLoading] = useState(false);
  const [date, setDate] = useState(moment().format("YYYY-MM-DD"));
  const [modal, setModal] = useState<"edit" | "delete" | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [editAvailable, setEditAvailable] = useState(false);
  const [editedData, setEditedData] = useState({
    phone: "",
    date: "",
    name: "",
    guardianName: "",
    token_number: "",
    booking_type: "normal" as BookingType,
  });
  const captureRef = useRef<HTMLDivElement>(null);
  const fetchCleanup = useRef<(() => void) | null>(null);

  const fetchAppointments = useCallback(() => {
    fetchCleanup.current?.();
    setLoading(true);
    fetchCleanup.current = apiSubscribe<AppointmentRow[]>(
      getAllAppointments(date),
      {
        next: (rows) => {
          setLoading(false);
          const sorted = [...(Array.isArray(rows) ? rows : [])].sort(
            (a, b) => Number(a.token_number) - Number(b.token_number)
          );
          setData(sorted);
        },
        error: () => {
          setLoading(false);
          setAlert({ variant: "error", message: "Failed to load appointments." });
        },
      }
    );
  }, [date]);

  useEffect(() => {
    fetchAppointments();
    return () => fetchCleanup.current?.();
  }, [fetchAppointments]);

  const openEdit = (row: AppointmentRow) => {
    setSelectedId(row.ID);
    setEditAvailable(false);
    setEditedData({
      date: row.Date,
      phone: row.Phone,
      name: row.Name,
      guardianName: row.GuardianName,
      token_number: String(row.token_number),
      booking_type: (row.booking_type === "urgent" ? "urgent" : "normal") as BookingType,
    });
    setModal("edit");
  };

  const handleDelete = () => {
    if (!selectedId) return;
    setLoading(true);
    apiSubscribe(
      deleteAppointment(selectedId),
      {
        next: () => {
          setModal(null);
          setAlert({ variant: "success", message: "Appointment deleted." });
          fetchAppointments();
        },
        error: () => {
          setLoading(false);
          setAlert({ variant: "error", message: "Delete failed." });
        },
      }
    );
  };

  const handleUpdate = () => {
    if (!selectedId || !editAvailable) {
      setAlert({ variant: "error", message: "Check availability before saving changes." });
      return;
    }
    setLoading(true);
    const isoDate =
      editedData.date.includes("-") && editedData.date.split("-")[0].length === 2
        ? moment(editedData.date, "DD-MM-YYYY").format("YYYY-MM-DD")
        : editedData.date;
    apiSubscribe(
      updateAppointment(selectedId, {
        name: editedData.name,
        phone: editedData.phone,
        guardianName: editedData.guardianName,
        date: isoDate,
        booking_type: editedData.booking_type,
      }),
      {
        next: (body: ApiMessageResponse) => {
          setLoading(false);
          if (body?.error) {
            setAlert({ variant: "error", message: body.error });
            return;
          }
          setModal(null);
          setAlert({
            variant: "success",
            message: body?.message || "Appointment updated.",
          });
          fetchAppointments();
        },
        error: () => {
          setLoading(false);
          setAlert({ variant: "error", message: "Update failed." });
        },
      }
    );
  };

  const exportImage = async () => {
    if (!captureRef.current) return;
    try {
      const canvas = await html2canvas(captureRef.current, { scale: 2 });
      const link = document.createElement("a");
      link.href = canvas.toDataURL("image/png");
      link.download = `appointments-${date}.png`;
      link.click();
    } catch {
      setAlert({ variant: "error", message: "Could not export image. Try again." });
    }
  };

  const checkEditDate = () => {
    const checkDate =
      editedData.date.includes("-") && editedData.date.split("-")[0].length === 4
        ? editedData.date
        : moment(editedData.date, "DD-MM-YYYY").format("YYYY-MM-DD");
    apiSubscribe<AvailabilityResponse>(
      getAvailability(checkDate, editedData.booking_type),
      {
        next: (response) => {
          const result = availabilityMessage(editedData.booking_type, response);
          setEditAvailable(result.available);
          setAlert({
            variant: result.available ? "success" : "error",
            message: result.message,
          });
        },
        error: () => {
          setEditAvailable(false);
          setAlert({ variant: "error", message: "Could not check availability." });
        },
      }
    );
  };

  const editDateValue =
    editedData.date.length === 10 && editedData.date.split("-")[0].length === 2
      ? moment(editedData.date, "DD-MM-YYYY").format("YYYY-MM-DD")
      : editedData.date;

  return (
    <>
      <LoadingOverlay show={loading} />

      {alert && (
        <Alert variant={alert.variant} onDismiss={() => setAlert(null)}>
          {alert.message}
        </Alert>
      )}

      <div className="flex items-center gap-2 rounded-xl border border-brand-100 bg-surface p-2 shadow-soft">
        <Input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="min-w-0 flex-1"
          aria-label="Appointment date"
        />
        <span className="flex h-7 min-w-[1.75rem] shrink-0 items-center justify-center rounded-full bg-brand-600 px-2 text-2xs font-bold tabular-nums text-white">
          {data.length}
        </span>
        <button
          type="button"
          onClick={exportImage}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-accent-blue-light text-accent-blue-dark hover:opacity-80"
          aria-label="Export PNG"
        >
          <FontAwesomeIcon icon={faDownload} className="text-sm" />
        </button>
        <button
          type="button"
          onClick={fetchAppointments}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand-100 text-brand-700 hover:opacity-80"
          aria-label="Refresh"
        >
          <FontAwesomeIcon icon={faRotateRight} className="text-sm" />
        </button>
      </div>

      <p className="text-2xs font-medium text-brand-600">
        {moment(date).format("ddd, DD MMM YYYY")}
      </p>

      {data.length === 0 && !loading ? (
        <EmptyState title="No appointments" description="No bookings scheduled for this date." />
      ) : (
        <ul className="divide-y divide-brand-100 overflow-hidden rounded-xl border border-brand-100 bg-surface shadow-soft">
          {data.map((row, index) => {
            const urgent = row.booking_type === "urgent";
            return (
              <li
                key={row.ID}
                className={`flex items-center gap-2 px-3 py-2 ${
                  urgent
                    ? "bg-status-danger-bg/40"
                    : index % 2 === 0
                      ? "bg-brand-50/30"
                      : "bg-surface"
                }`}
              >
                <span
                  className={`w-11 shrink-0 rounded-md px-1 py-0.5 text-center text-xs font-bold tabular-nums ${
                    urgent
                      ? "bg-accent-rose-light text-accent-rose-dark"
                      : "bg-brand-100 text-brand-800"
                  }`}
                >
                  {formatTokenLabel(row.token_number, row.booking_type)}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium leading-tight text-content-primary">
                    {row.Name}
                  </p>
                  <p className="truncate text-2xs leading-tight text-content-muted">
                    {row.GuardianName} · {row.Phone}
                  </p>
                </div>
                <div className="flex shrink-0 gap-0.5">
                  <button
                    type="button"
                    onClick={() => openEdit(row)}
                    className="flex h-8 w-8 items-center justify-center rounded-md text-accent-blue hover:bg-accent-blue-light"
                    aria-label="Edit"
                  >
                    <FontAwesomeIcon icon={faPen} className="text-xs" />
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedId(row.ID);
                      setModal("delete");
                    }}
                    className="flex h-8 w-8 items-center justify-center rounded-md text-content-muted hover:bg-status-danger-bg hover:text-status-danger"
                    aria-label="Delete"
                  >
                    <FontAwesomeIcon icon={faTrash} className="text-xs" />
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <div
        ref={captureRef}
        className="pointer-events-none fixed left-[-9999px] top-0 w-[480px] bg-surface p-6"
        aria-hidden
      >
        <h2 className="mb-4 text-base font-semibold text-content-primary">
          Appointments — {moment(date).format("DD-MM-YYYY")}
        </h2>
        <table className="w-full border-collapse text-xs">
          <thead>
            <tr className="border-b border-border text-left text-content-muted">
              <th className="p-2">Token</th>
              <th className="p-2">Name</th>
              <th className="p-2">Guardian</th>
              <th className="p-2">Phone</th>
            </tr>
          </thead>
          <tbody>
            {data.map((row) => (
              <tr key={row.ID} className="border-b border-border">
                <td className="p-2">{formatTokenLabel(row.token_number, row.booking_type)}</td>
                <td className="p-2">{row.Name}</td>
                <td className="p-2">{row.GuardianName}</td>
                <td className="p-2">{row.Phone}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal
        open={modal === "delete"}
        onClose={() => setModal(null)}
        title="Delete appointment"
        description="This action cannot be undone."
        footer={
          <ModalFooter>
            <Button variant="secondary" onClick={() => setModal(null)}>Cancel</Button>
            <Button variant="danger" onClick={handleDelete}>Delete</Button>
          </ModalFooter>
        }
      />

      <Modal
        open={modal === "edit"}
        onClose={() => setModal(null)}
        title="Edit appointment"
        size="lg"
        footer={
          <ModalFooter>
            <Button variant="secondary" onClick={() => setModal(null)}>Cancel</Button>
            <Button onClick={handleUpdate} disabled={!editAvailable}>Save changes</Button>
          </ModalFooter>
        }
      >
        <Alert variant="info" className="mb-4">
          {bookingTypeLabel(editedData.booking_type)} — Token{" "}
          {formatTokenLabel(editedData.token_number, editedData.booking_type)}
        </Alert>
        <div className="space-y-4">
          {urgentEnabled && (
            <div>
              <p className="mb-2 text-sm font-medium text-content-primary">Booking type</p>
              <div className="grid grid-cols-2 gap-2">
                {(["normal", "urgent"] as BookingType[]).map((type) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => {
                      setEditedData({ ...editedData, booking_type: type });
                      setEditAvailable(false);
                    }}
                    className={`rounded-xl border-2 px-3 py-2 text-sm font-semibold capitalize transition-colors ${
                      editedData.booking_type === type
                        ? type === "urgent"
                          ? "border-accent-rose bg-accent-rose-light text-accent-rose-dark"
                          : "border-accent-emerald bg-accent-emerald-light text-accent-emerald-dark"
                        : "border-border bg-surface text-content-muted"
                    }`}
                  >
                    {type}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="flex flex-col gap-2">
            <Input
              label="Date"
              type="date"
              value={editDateValue}
              onChange={(e) => {
                setEditedData({ ...editedData, date: e.target.value });
                setEditAvailable(false);
              }}
            />
            <Button type="button" variant="secondary" fullWidth onClick={checkEditDate}>
              Check availability
            </Button>
          </div>
          <Input
            label="Phone"
            value={editedData.phone}
            onChange={(e) => setEditedData({ ...editedData, phone: e.target.value })}
          />
          <Input
            label="Patient name"
            value={editedData.name}
            onChange={(e) => setEditedData({ ...editedData, name: e.target.value })}
          />
          <Input
            label="Guardian name"
            value={editedData.guardianName}
            onChange={(e) => setEditedData({ ...editedData, guardianName: e.target.value })}
          />
        </div>
      </Modal>
    </>
  );
}

export default AllAppointments;
